/**
 * @file Adapter de données du flow de réservation pour le widget embarquable.
 * @module widgets.TableReservation.adapters.useWidgetReservationDataAdapter
 *
 * Branche les opérations « nues » attendues par le flow partagé sur l'API
 * externe authentifiée par clé (axiosWidget + en-tête `x-api-key`). companyId et
 * apiKey sont capturés en closure : le flow appelle les opérations sans jamais
 * les manipuler. Le site public fournit un adapter équivalent câblé sur les
 * endpoints publics par slug.
 */
import {useMemo, useRef} from 'react';
import axiosWidgetInstance, {withApiKey, widgetEndpoints} from 'packages/common/utils/axiosWidget';
import {
  useExternalAvailableDates,
  useExternalNextAvailableDates,
  useExternalTimeSlots,
  useExternalBookableRooms,
  useExternalCreateReservation,
} from 'packages/common/hooks/api/widgets/restaurants/Reservations';
import {useEvaluateCardHold, usePreparePayment, useConfirmPayment} from 'packages/common/hooks/api/widgets/restaurants/Payment';
import {useExternalCreateWaitlistEntry} from 'packages/common/hooks/api/widgets/restaurants/Waitlist';
import {useExternalSyncReservationDraft} from 'packages/common/hooks/api/widgets/restaurants/ReservationDraft';
import {readSlotFormulasResponse} from 'packages/common/utils/restaurant/formulaConstraints';

/**
 * Construit l'adapter de données injecté dans `<ReservationDataProvider>`.
 *
 * @param {string} companyId - Identifiant de l'établissement
 * @param {string} apiKey - Clé API publique du widget
 * @return {Object} Adapter d'opérations de données (identité stable par companyId/apiKey)
 */
export function useWidgetReservationDataAdapter(companyId, apiKey) {
  const {getAvailableDates} = useExternalAvailableDates();
  const {getNextAvailableDates} = useExternalNextAvailableDates();
  const {getTimeSlotsForDate} = useExternalTimeSlots();
  const {getBookableRooms} = useExternalBookableRooms();
  const evaluateCardHold = useEvaluateCardHold();
  const preparePayment = usePreparePayment();
  const confirmPayment = useConfirmPayment();
  const {createReservation} = useExternalCreateReservation();
  const {createWaitlistEntry} = useExternalCreateWaitlistEntry();
  const syncReservationDraft = useExternalSyncReservationDraft();

  // Les fonctions des hooks impératifs (dates/slots/create/waitlist) sont
  // recréées à chaque rendu. On les lit via une ref afin d'exposer des
  // opérations d'identité stable : les effets du flow (usePaymentRequirement,
  // useFormulaRequirement…) qui ont ces opérations en dépendance ne se relancent
  // alors que sur un vrai changement de paramètre, jamais sur un simple rendu.
  const apiRef = useRef();
  apiRef.current = {
    getAvailableDates,
    getNextAvailableDates,
    getTimeSlotsForDate,
    getBookableRooms,
    evaluateCardHold,
    preparePayment,
    confirmPayment,
    createReservation,
    createWaitlistEntry,
    syncReservationDraft,
  };

  return useMemo(() => ({
    getAvailableDates: (params) => apiRef.current.getAvailableDates(companyId, params, apiKey),
    getNextAvailableDates: (params, options) => apiRef.current.getNextAvailableDates(companyId, params, apiKey, options),
    getTimeSlots: (params) => apiRef.current.getTimeSlotsForDate(companyId, params, apiKey),
    getBookableRooms: (params) => apiRef.current.getBookableRooms(companyId, params, apiKey),
    getAvailableFormulas: async ({service, date, partySize, time, dateTime}, {signal} = {}) => {
      const response = await axiosWidgetInstance.get(
          widgetEndpoints.externalApi.restaurant.reservation.formula.available(companyId),
          {...withApiKey(apiKey), params: {service, date, partySize, time, dateTime}, signal},
      );
      return readSlotFormulasResponse(response.data);
    },
    getFormulaDates: async ({service, year, month}, {signal} = {}) => {
      const response = await axiosWidgetInstance.get(
          widgetEndpoints.externalApi.restaurant.reservation.formula.dates(companyId),
          {...withApiKey(apiKey), params: {service, year, month}, signal},
      );
      return response.data?.success && Array.isArray(response.data.dates) ? response.data.dates : [];
    },
    evaluateCardHold: (payload) => apiRef.current.evaluateCardHold(companyId, payload, apiKey),
    preparePayment: (payload) => apiRef.current.preparePayment(companyId, payload, apiKey),
    confirmPayment: (payload) => apiRef.current.confirmPayment(companyId, payload, apiKey),
    createReservation: (payload) => apiRef.current.createReservation(companyId, payload, apiKey),
    createWaitlistEntry: (payload) => apiRef.current.createWaitlistEntry(companyId, payload, apiKey),
    syncReservationDraft: (payload) => apiRef.current.syncReservationDraft(companyId, payload, apiKey),
  }), [companyId, apiKey]);
}
